import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import DomeGallery from './DemoGallery';

const galleryImages = [
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135206/Screenshot_2026-08-19_154904.jpg',
    alt: 'Racing car on track',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135206/Screenshot_2026-08-19_154946.jpg',
    alt: 'Formula car front view',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135206/Screenshot_2026-08-19_154925.jpg',
    alt: 'Sports car close-up',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135206/Screenshot_2026-08-19_154800.jpg',
    alt: 'Racing wheels',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135206/Screenshot_2026-08-19_155017.jpg',
    alt: 'Motorbike racing',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135206/Screenshot_2026-08-19_155034.jpg',
    alt: 'Race car speed',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135207/Screenshot_2026-08-19_155130.jpg',
    alt: 'Track racing',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135207/Screenshot_2026-08-19_155238.jpg',
    alt: 'Sports car in motion',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135208/Screenshot_2026-08-19_155301.jpg',
    alt: 'Race car detail',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135209/Screenshot_2026-08-19_155330.jpg',
    alt: 'Racing action',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135209/Screenshot_2026-08-19_155348.jpg',
    alt: 'Off-road motorbike',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135210/Screenshot_2026-08-19_155400.jpg',
    alt: 'Desert rally',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135211/Screenshot_2026-08-19_155419.jpg',
    alt: 'Helmet portrait',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135212/Screenshot_2026-08-19_155442.jpg',
    alt: 'Race podium',
  },
  {
    src: 'https://res.cloudinary.com/px1co5qc/image/upload/v1787135212/Screenshot_2026-08-19_155459.jpg',
    alt: 'Rally car dirt',
  },
];

const teams = [
  'KTM',
  'DREAM MACHINES',
  'APEX & 6KIOM',
  'XMZ RACING',
  'TORQUE',
  'X TORQUE',
  'YAMAHA',
  'CEAT',
  'MOTUL',
  'TVS RACING',
];

const skills = [
  'OFF-ROAD BIKE RIDING',
  'RACE & FITNESS TRAINING',
  'TEAM MANAGEMENT',
  'NATIONAL LEVEL ATHLETE (HOCKEY)',
  'HEALING',
  'WHOLISTIC TEACHER',
  'MOTIVATIONAL SPEAKER',
];

const Gallery = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, delay: i * 0.1, ease: [0.25, 1, 0.5, 1] },
    }),
  };

  return (
    <section id="gallery" className="relative py-20 md:py-28 bg-darker overflow-hidden text-white">
      {/* Inline styles for continuous marquee animations without external Tailwind configs */}
      <style>{`
        @keyframes galleryMarquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        @keyframes galleryMarqueeReverse {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0%); }
        }
        .animate-gallery-marquee {
          display: flex;
          width: max-content;
          animation: galleryMarquee 28s linear infinite;
        }
        .animate-gallery-marquee-reverse {
          display: flex;
          width: max-content;
          animation: galleryMarqueeReverse 32s linear infinite;
        }
        .animate-gallery-marquee:hover,
        .animate-gallery-marquee-reverse:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Background Atmosphere */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0505] to-black"></div>
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl bg-primary"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-5 blur-3xl bg-white"></div>
      </div>

      {/* Checkered pattern top-right */}
      <div className="absolute top-8 right-8 grid grid-cols-6 gap-[2px] opacity-20 pointer-events-none z-10">
        {[...Array(24)].map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 ${
              (Math.floor(i / 6) + i) % 2 === 0 ? 'bg-white' : 'bg-transparent'
            }`}
          ></div>
        ))}
      </div>

      {/* Section Header (Contained) */}
      <div ref={ref} className="relative max-w-7xl mx-auto px-4 md:px-8 mb-8 md:mb-10">
        <div className="mb-8 md:mb-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="flex items-center gap-3 mb-4"
          >
            <div className="w-8 md:w-12 h-[2px] bg-primary"></div>
            <span className="text-primary font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase">
              Moments
            </span>
          </motion.div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <motion.h2
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-[0.9] tracking-tight"
            >
              GAL<span className="text-primary">LERY</span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              className="text-sm md:text-base text-white/50 max-w-md leading-relaxed"
            >
              A visual journey through races, podiums, and defining moments.
              Drag to explore the dome — click any image to enlarge.
            </motion.p>
          </div>
        </div>

        {/* Instruction Bar */}
        <motion.div
          variants={fadeUp}
          custom={3}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            
            <div className="hidden md:flex items-center gap-2 text-[10px] tracking-[0.2em] text-white/40 uppercase font-mono">
              <span>Drag</span>
              <span className="text-primary">·</span>
              <span>Click</span>
              <span className="text-primary">·</span>
              <span>Explore</span>
            </div>
          </div>

          <span className="text-[10px] md:text-xs tracking-[0.2em] text-white/40 font-mono">
            {galleryImages.length} MOMENTS
          </span>
        </motion.div>
      </div>

      {/* FULL WIDTH DOME GALLERY CONTAINER */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 1, 0.5, 1] }}
        className="relative w-full border-y border-white/10 bg-gradient-to-b from-black/60 via-[#0a0a0a] to-black/60 my-6"
      >
        <div
          className="relative w-full"
          style={{ height: 'clamp(520px, 75vh, 850px)' }}
        >
          <DomeGallery
            images={galleryImages}
            fit={0.65}
            grayscale={false}
            overlayBlurColor="#0a0a0a"
            imageBorderRadius="16px"
            openedImageBorderRadius="20px"
            openedImageWidth="480px"
            openedImageHeight="480px"
            minRadius={550}
            dragSensitivity={18}
            maxVerticalRotationDeg={8}
          />
        </div>

        {/* Full-width decorative corner accents */}
        <div className="absolute top-4 left-6 flex items-center gap-2 pointer-events-none z-10">

          
        </div>
        <div className="absolute top-4 right-6 pointer-events-none z-10">
          <span className="text-[9px] tracking-[0.3em] text-white/30 uppercase font-mono">
            FULL_SCREEN_CANVAS
          </span>
        </div>
      </motion.div>

      {/* DYNAMIC MOTORSPORT MARQUEE SECTION */}
      <div className="relative py-8 space-y-4 overflow-hidden select-none">
        {/* Marquee 1: TEAMS */}
        <div className="relative bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border-y border-primary/30 py-3.5 backdrop-blur-md transform -skew-y-1">
          <div className="animate-gallery-marquee flex items-center gap-6 whitespace-nowrap">
            {[...teams, ...teams, ...teams, ...teams].map((team, index) => (
              <div key={index} className="flex items-center gap-6">
                <span className="text-xs md:text-sm font-mono font-black tracking-[0.25em] text-white uppercase flex items-center gap-2">
                  <span className="text-primary text-xs">//</span>
                  {team}
                </span>
                <span className="text-primary text-xs font-bold">★</span>
              </div>
            ))}
          </div>
          {/* Label Tag */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 bg-primary px-2 py-0.5 rounded text-[8px] font-mono font-black text-black tracking-widest uppercase z-20 pointer-events-none hidden sm:block">
            TEAMS
          </div>
        </div>

        {/* Marquee 2: SKILLS & STRENGTHS */}
        <div className="relative bg-zinc-900/80 border-y border-white/10 py-3.5 backdrop-blur-md transform -skew-y-1">
          <div className="animate-gallery-marquee-reverse flex items-center gap-8 whitespace-nowrap">
            {[...skills, ...skills, ...skills, ...skills].map((skill, index) => (
              <div key={index} className="flex items-center gap-8">
                <span className="text-xs md:text-sm font-heading tracking-[0.2em] font-bold text-zinc-300 uppercase flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/80"></span>
                  {skill}
                </span>
                <span className="text-zinc-600 font-mono text-xs">•</span>
              </div>
            ))}
          </div>
          {/* Label Tag */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 bg-zinc-800 border border-white/20 px-2 py-0.5 rounded text-[8px] font-mono font-black text-white tracking-widest uppercase z-20 pointer-events-none hidden sm:block">
            STRENGTHS
          </div>
        </div>
      </div>

      {/* Bottom Tag */}
      <motion.div
        variants={fadeUp}
        custom={6}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className="mt-8 flex items-center justify-center gap-3"
      >
        <div className="w-8 h-[1px] bg-white/20"></div>
        <span className="text-[10px] md:text-xs tracking-[0.3em] text-white/40 uppercase font-mono">
          Every frame · Every moment · Every race
        </span>
        <div className="w-8 h-[1px] bg-white/20"></div>
      </motion.div>
    </section>
  );
};

export default Gallery;